import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { X, Camera, AlertCircle } from 'lucide-react';

const QRScanner = ({ onScan, onClose }) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [permissionGranted, setPermissionGranted] = useState(false);
  const scannedRef = useRef(false); // Use ref instead of state for immediate access
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    // Start scanner directly - html5-qrcode will request camera permission
    startScanner();
    
    return () => {
      console.log('QRScanner unmounting - forcing cleanup');
      stopScanner();
      
      // Force stop any remaining media tracks
      const qrReaderElement = document.getElementById('qr-reader');
      if (qrReaderElement) {
        const videos = qrReaderElement.querySelectorAll('video');
        videos.forEach(video => {
          if (video.srcObject) {
            const tracks = video.srcObject.getTracks();
            tracks.forEach(track => track.stop());
            video.srcObject = null;
          }
        });
      }
    };
  }, []);

  const startScanner = async () => {
    try {
      const html5QrCode = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = html5QrCode;

      // Html5Qrcode will request camera permission - this is the ONLY permission request
      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          // Prevent multiple scans using ref for immediate check
          if (scannedRef.current) {
            console.log('Already scanned, ignoring duplicate scan');
            return;
          }
          
          console.log('QR Code scanned:', decodedText);
          scannedRef.current = true; // Set immediately to block subsequent scans
          
          // Stop scanner immediately and close the scanner UI
          stopScanner().then(() => {
            // Close the scanner UI immediately
            onClose();
            // Call callback after scanner is fully stopped
            setTimeout(() => {
              onScan(decodedText);
            }, 100);
          });
        },
        (errorMessage) => {
          // Ignore scan errors (happens when no QR code is in view)
        }
      );
      setScanning(true);
      setPermissionGranted(true);
      setError('');
    } catch (err) {
      console.error('Error starting scanner:', err);
      // Error hidden - silent fail, just close the scanner
      onClose();
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        console.log('Stopping scanner and releasing camera...');
        
        const qrReaderElement = document.getElementById('qr-reader');
        
        // CRITICAL: Stop the html5-qrcode scanner first to release camera
        if (scanning || html5QrCodeRef.current.isScanning) {
          try {
            await html5QrCodeRef.current.stop();
            console.log('Html5Qrcode stopped');
          } catch (stopErr) {
            console.error('Error calling stop():', stopErr);
          }
        }
        
        // Then forcefully stop all video tracks
        if (qrReaderElement) {
          const videos = qrReaderElement.querySelectorAll('video');
          videos.forEach(video => {
            if (video.srcObject) {
              const stream = video.srcObject;
              const tracks = stream.getTracks();
              tracks.forEach(track => {
                console.log('Force stopping track:', track.kind, track.readyState);
                track.stop();
                console.log('Track stopped, new state:', track.readyState);
              });
              video.srcObject = null;
              video.load(); // Reset video element
            }
          });
        }
        
        // Clear the scanner instance
        try {
          await html5QrCodeRef.current.clear();
        } catch (clearErr) {
          console.error('Error clearing scanner:', clearErr);
        }
        
        html5QrCodeRef.current = null;
        setScanning(false);
        
        // Final cleanup: Remove any remaining video/canvas elements
        if (qrReaderElement) {
          const videos = qrReaderElement.querySelectorAll('video');
          const canvases = qrReaderElement.querySelectorAll('canvas');
          videos.forEach(video => {
            video.pause();
            video.srcObject = null;
            video.remove();
          });
          canvases.forEach(canvas => canvas.remove());
        }
        
        // Extra iOS cleanup - get all media streams globally
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          console.log('Performing extra iOS camera cleanup');
        }
        
        console.log('Camera fully released');
      } catch (err) {
        console.error('Error stopping scanner:', err);
        // Even if there's an error, try to forcefully release camera
        const qrReaderElement = document.getElementById('qr-reader');
        if (qrReaderElement) {
          const videos = qrReaderElement.querySelectorAll('video');
          videos.forEach(video => {
            if (video.srcObject) {
              video.srcObject.getTracks().forEach(track => track.stop());
              video.srcObject = null;
            }
            video.remove();
          });
        }
      }
    }
  };

  const handleClose = async () => {
    console.log('Close button clicked - cleaning up camera');
    await stopScanner();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-md">
        <Button
          onClick={handleClose}
          data-testid="qr-scanner-close"
          className="absolute -top-12 right-0 bg-white/10 hover:bg-white/20 text-white border-none"
          size="sm"
        >
          <X className="w-4 h-4 mr-2" />
          Close
        </Button>

        <div className="bg-slate-900 rounded-2xl p-6 border-2 border-cyan-500/30">
          <div className="text-center mb-4">
            <Camera className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
            <h3 className="text-xl font-bold text-cyan-400 digital-font">SCAN QR CODE</h3>
            <p className="text-slate-400 text-sm mt-2">
              {permissionGranted 
                ? 'Point your camera at the QR code' 
                : 'Requesting camera access...'}
            </p>
          </div>

          <div>
            <div
              id="qr-reader"
              data-testid="qr-reader"
              className="rounded-lg overflow-hidden border-2 border-slate-700 bg-black"
              style={{ width: '100%', minHeight: '300px' }}
            />
            
            {!scanning && permissionGranted && (
              <div className="mt-4 text-center">
                <div className="inline-flex items-center gap-2 text-yellow-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>Starting camera...</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScanner;
